import { useContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { nanoid } from 'nanoid';
import Bluebird from 'bluebird';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import * as ApiModels from '@typings/api-models';
import useNoodleApi from '@hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import { useParams } from 'next/navigation';
import * as tsClient from '@tsClient';
import * as format from '@format';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import { useQueryState } from 'nuqs';
import { keys } from 'lodash';
import {
  type HookResponse,
  type ResourceType,
  type ListViewRegularItem,
  type TableCell,
  type TableRow,
  type TableColumn,
  addHeaderKeyToCell,
} from '../Workflow/common';

type ThisUserWorkflow = Awaited<ReturnType<typeof tsClient.creators.getUserWorkflows>>['items'][number];

type PendingClientTasksFilter = 'custom-terms' | 'document-request-user' | 'form-request' | 'invoice';

interface StatusProperty {
  label: string;
  color: string;
}

const formatUserWorkflowContext = (userWorkflowContext: Pick<ApiModels.UserWorkflowContext, 'label' | 'value' | 'type'>[]): string => {
  let returnString = '';
  userWorkflowContext.forEach((context, index) => {
    returnString = returnString.concat(`${context.label}: ${context.value}${index === userWorkflowContext.length - 1 ? '' : ', '}`);
  });
  return returnString;
};

const StatusToBadge: Record<string, StatusProperty> = {
  'paused': { color: 'var( --color-workflow-paused)', label: 'Paused' },
};

const statusToBadge = ({ pausedUntil, status }: { pausedUntil: string | null, status: Pick<ApiModels.CustomStatus, 'title' | 'colour'> | undefined }): ListViewRegularItem['badge'] | undefined => {
  if (pausedUntil) {
    return StatusToBadge.paused;
  } if (status) {
    return { color: status.colour, label: status.title };
  }
  return undefined;
};

const TABLE_COLUMNS: TableColumn[] = [
  {
    key: 'workflow',
    label: 'Workflow Title',
  },
  // {
  //   key: 'assignees',
  //   label: 'Assigned to',
  // },
  {
    key: 'status',
    label: 'Workflow Status',
  },
  {
    key: 'contact',
    label: 'Contact',
  },
  {
    key: 'context',
    label: 'Context',
  },
  {
    key: 'progress',
    label: 'Progress',
  },
  {
    key: 'recent-activity',
    label: 'Recent activity',
  },
  {
    key: 'pending-client-tasks',
    label: 'Pending Client Tasks',
  },
];

const curriedAddHeaderKeyToCell = addHeaderKeyToCell(TABLE_COLUMNS);

const userWorkflowToTableRow = ({
  hasAssignedTasksByUserWorkflow,
  userWorkflow,
  statuses,
}: {
  hasAssignedTasksByUserWorkflow: Record<string, boolean | 'fetching'>;
  userWorkflow: Awaited<ReturnType<typeof tsClient.creators.getUserWorkflows>>['items'][number];
  statuses: Array<Pick<ApiModels.CustomStatus, 'slug' | 'title' | 'colour'>> | null;
}): TableRow => {
  const userWorkflowStatus = statuses?.find((status) => status.slug === userWorkflow.status) ?? undefined;
  const statusBadge = statusToBadge({ pausedUntil: userWorkflow.pausedUntil, status: userWorkflowStatus });
  const progress = userWorkflow.status === 'data-collection' || userWorkflow.pausedUntil
    ? userWorkflow.progress * 100
    : undefined;
  const hasAssignedTasks = hasAssignedTasksByUserWorkflow[userWorkflow.id] !== 'fetching' && hasAssignedTasksByUserWorkflow[userWorkflow.id];
  const needsAttention = userWorkflow.status === 'data-collection' ? Boolean(hasAssignedTasks && hasAssignedTasks !== 'fetching') : false;

  const cells: TableCell[] = [
    {
      cta: 'Open workflow',
      href: getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: userWorkflow.id }),
      label: userWorkflow.workflow.name,
      type: 'open-details',
    },
    // {
    //   assignees: userWorkflow.workflow.assignees || [],
    //   type: 'assignees',
    // },
    statusBadge
      ? {
        type: 'badge',
        ...statusBadge,
      }
      : {
        type: 'empty',
      },
    {
      content: userWorkflow.person.name || '—',
      type: 'text',
    },
    {
      content: formatUserWorkflowContext(userWorkflow.context),
      type: 'text',
    },
    typeof progress === 'number'
      ? {
        progress,
        type: 'progress',
      }
      : { type: 'empty' },
    {
      content: format.datetimeLocalTZ.withoutTimezone({datetime:userWorkflow.lastActivityAt}),
      type: 'text',
    },
    {
      content: `${userWorkflow.numPendingClientTasks}`,
      type: 'text',
    },
  ];

  return {
    cells: cells.map(curriedAddHeaderKeyToCell),
    id: userWorkflow.id,
    state: needsAttention ? 'warning' : undefined,
  };
};

const getDetailPagePath: HookResponse['getDetailPagePath'] = ({ id }) => getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: id });

const useActivity = (
  { resourceType }:
  { resourceType: ResourceType },
): HookResponse => {
  const useTableView = useFeatureFlag('table-view');
  const pathParams = useParams<{ userWorkflowId?: string }>();
  const { creator, customStatuses } = useContext(DashboardContext);
  const creatorId = creator?.id;
  const [countsByStatus, setCountsByStatus] = useState<Record<string, number>>({});
  const [userWorkflows, setUserWorkflows] = useState<ThisUserWorkflow[]>([]);
  const [itemsCount, setItemsCount] = useState<number | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [hasAssignedTasksByUserWorkflow, setHasAssignedTasksByUserWorkflow] = useState<Record<string, boolean | 'fetching'>>({});
  const [numPages, setNumPages] = useState(0);
  const mostRecentRequestRef = useRef<string | null>(null);
  const { getData: getUserWorkflowsFn } = useNoodleApi(tsClient.creators.getUserWorkflows);
  const { getData: getUserWorkflowProgressFn } = useNoodleApi(tsClient.workflows.getUserWorkflowProgress);
  const [customTermsFilterValue, setCustomTermsFilterValue] = useQueryState('custom-terms');
  const [documentRequestUserFilterValue, setDocumentRequestUserFilterValue] = useQueryState('document-request-user');
  const [formRequestFilterValue, setFormRequestFilterValue] = useQueryState('form-request');
  const [invoiceFilterValue, setInvoiceFilterValue] = useQueryState('invoice');

  const selectedClientTaskFilters = useMemo(() => {
    const allFilters: Record<PendingClientTasksFilter, string | null> = {
      'custom-terms': customTermsFilterValue,
      'document-request-user': documentRequestUserFilterValue,
      'form-request': formRequestFilterValue,
      'invoice': invoiceFilterValue,
    };

    return keys(allFilters).filter(key => allFilters[key as unknown as PendingClientTasksFilter] === 'true');
  }, [customTermsFilterValue, documentRequestUserFilterValue, formRequestFilterValue, invoiceFilterValue]);

  const [initiatedByPersonId] = useQueryState('initiatedByPersonId');

  const selectedId = pathParams?.userWorkflowId;

  const fetchWorkflows = useCallback(
    async ({ page: newPage, search: newSearch, statuses }: { page: number; search: string; statuses: string[] }): Promise<void> => {
      if (creatorId && resourceType === 'activity') {
        setCurrentPage(newPage);
        const thisRequestId = nanoid();
        mostRecentRequestRef.current = thisRequestId;
        setIsFetching(true);
        setUserWorkflows([]);
        setCurrentPage(newPage);

        const response = await getUserWorkflowsFn({
          clientTaskFilters: selectedClientTaskFilters,
          creatorId,
          initiatedByPersonId,
          page: newPage,
          perPage: 25,
          search: newSearch,
          status: statuses,
        });

        if (thisRequestId === mostRecentRequestRef.current) {
          if (response.data) {
            setNumPages(response.data.numPages);
            setUserWorkflows(response.data.items);
            setItemsCount(response.data.numItems);
          }
          setIsFetching(false);
        }
      }
    },
    [creatorId, resourceType, getUserWorkflowsFn, initiatedByPersonId, selectedClientTaskFilters],
  );

  const handleNewPage = (newPage: number): void => {
    fetchWorkflows({
      page: newPage,
      search: searchTerm,
      statuses: selectedStatuses,
    });
  };

  useEffect(() => {
    const loadAllCounts = async (): Promise<void> => {
      if (creatorId && resourceType === 'activity' && customStatuses) {
        const getParams = {
          creatorId,
          initiatedByPersonId,
          page: 1,
          perPage: 1,
          search: searchTerm,
        };
        await Bluebird.map(
          [...customStatuses, { slug: 'paused' }],
          async status => {
            const response = await getUserWorkflowsFn({
              ...getParams,
              status: [status.slug],
            });
            setCountsByStatus(prev => ({ ...prev, [status.slug]: response.data?.numItems ?? 0 }));
          },
          { concurrency: 5 },
        );
      }
    };
    loadAllCounts();
  }, [customStatuses, creatorId, getUserWorkflowsFn, initiatedByPersonId, searchTerm, resourceType]);

  useEffect(() => {
    fetchWorkflows({
      page: 1,
      search: searchTerm,
      statuses: selectedStatuses,
    });
  }, [fetchWorkflows, searchTerm, selectedStatuses]);

  useEffect(() => {
    const needProgressIds = userWorkflows
      .filter(userWorkflow => hasAssignedTasksByUserWorkflow[userWorkflow.id] === undefined)
      .map(userWorkflow => userWorkflow.id);

    Bluebird.mapSeries(
      needProgressIds,
      async userWorkflowId => {
        setHasAssignedTasksByUserWorkflow(prev => ({ ...prev, [userWorkflowId]: 'fetching' }));
        const response = await getUserWorkflowProgressFn({ userWorkflowId });
        if (response.data) {
          const responseData = response.data;
          setHasAssignedTasksByUserWorkflow(prev => ({ ...prev, [userWorkflowId]: responseData.hasAssignedTasks }));
        }
      },
    );
    // intentionally not looking at hasAssignedTasksByUserWorkflow, only do this if the userWorkflows changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userWorkflows, getUserWorkflowProgressFn]);

  const pagination = {
    numPages,
    onChangePage: handleNewPage,
    page: currentPage,
  };

  const filterItems = useMemo(() => {
    const statuses: Array<{
      isChecked: boolean;
      key: string;
      label: string;
    }> = [
      ...(customStatuses ?? []).map(status => ({
        isChecked: selectedStatuses.includes(status.slug),
        key: status.slug,
        label: `${status.title} (${countsByStatus[status.slug] ?? 0})`,
      })),
      {
        isChecked: selectedStatuses.includes('paused'),
        key: 'paused',
        label: `Paused (${countsByStatus.paused ?? 0})`,
      },
    ];
    return statuses;
  }, [
    customStatuses,
    selectedStatuses,
    countsByStatus,
  ]);

  const clientTaskFilterItems = useMemo(() => {
    const clientTaskTypes: Array<{
      isChecked: boolean;
      key: string;
      label: string;
    }> = [
      {
        isChecked: selectedClientTaskFilters.includes('document-request-user'),
        key: 'document-request-user',
        label: `Upload Document Requests`,
      },
      {
        isChecked: selectedClientTaskFilters.includes('custom-terms'),
        key: 'custom-terms',
        label: `Retainer Term Requests`,
      },
      {
        isChecked: selectedClientTaskFilters.includes('form-request'),
        key: 'form-request',
        label: `Form Requests`,
      },
      {
        isChecked: selectedClientTaskFilters.includes('invoice'),
        key: 'invoice',
        label: `Invoice Requests`,
      },
    ];
    return clientTaskTypes;
  }, [
    selectedClientTaskFilters,
  ]);

  const filters: NonNullable<HookResponse['filters']> = {
    items: [
      {
        isMultiSelect: true,
        items: filterItems,
        label: 'Status',
        onChange: (newStatuses) => setSelectedStatuses(newStatuses),
      },
      {
        isMultiSelect: true,
        items: clientTaskFilterItems,
        label: 'Pending Client Tasks',
        onChange: (newFilters) => {
          if (newFilters.includes('custom-terms')) {
            setCustomTermsFilterValue('true');
          } else {
            setCustomTermsFilterValue('false');
          }

          if (newFilters.includes('document-request-user')) {
            setDocumentRequestUserFilterValue('true');
          } else {
            setDocumentRequestUserFilterValue('false');
          }

          if (newFilters.includes('form-request')) {
            setFormRequestFilterValue('true');
          } else {
            setFormRequestFilterValue('false');
          }

          if (newFilters.includes('invoice')) {
            setInvoiceFilterValue('true');
          } else {
            setInvoiceFilterValue('false');
          }
        },
      },
    ],
  };

  const tableRows = userWorkflows.map(userWorkflow =>
    userWorkflowToTableRow({
      hasAssignedTasksByUserWorkflow,
      statuses: customStatuses,
      userWorkflow,
    }),
  );

  return {
    filters,
    getDetailPagePath,
    ifNoItemsText: 'No activity',
    isFetchingList: isFetching,
    items: [],
    itemsCount,
    onSearch: setSearchTerm,
    pagination,
    selectedId,
    tableView: useTableView
      ? {
        columns: TABLE_COLUMNS,
        rows: tableRows,
      }
      : undefined,
  };
};

export default useActivity;
